import type {
  AnnouncementSeverity,
  PlatformAnnouncement,
} from '@/services/platform-announcement/model'
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

const TYPE_LABEL_DEFAULTS: Record<string, string> = {
  feature: 'NEW',
  news: 'NEWS',
  maintenance: 'MAINTENANCE',
  promotion: 'PROMOTION',
}

const SEVERITY_PALETTE: Record<AnnouncementSeverity, 'info' | 'success' | 'warning' | 'error'> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
}

interface Props {
  announcement: PlatformAnnouncement
  onDismiss: (id: string, permanent: boolean) => void
}

const PlatformAnnouncementBanner = ({ announcement, onDismiss }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const [dontShowAgain, setDontShowAgain] = useState(false)

  const paletteKey = SEVERITY_PALETTE[announcement.severity] ?? 'info'
  const mainColor = theme.palette[paletteKey].main
  const bgColor = alpha(mainColor, 0.1)
  const borderColor = alpha(mainColor, 0.4)

  return (
    <Box
      sx={{
        bgcolor: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: 2,
        px: 2.5,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <CampaignRoundedIcon sx={{ color: mainColor, fontSize: 22 }} />

      <Chip
        label={t(
          `platform-announcement.type.${announcement.type}`,
          TYPE_LABEL_DEFAULTS[announcement.type] ?? announcement.type.toUpperCase()
        )}
        size="small"
        sx={{
          bgcolor: mainColor,
          color: theme.palette.common.white,
          fontWeight: 600,
          fontSize: 11,
          height: 22,
        }}
      />

      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 14,
            color: mainColor,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '40%',
          }}
        >
          {announcement.title}
        </Typography>
        <Typography sx={{ mx: 0.5, color: mainColor, opacity: 0.5 }}>·</Typography>
        <Typography
          sx={{
            fontSize: 13,
            color: mainColor,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {announcement.summary}
        </Typography>
      </Stack>

      <Typography
        onClick={() => {
          void navigate(`/announcement/${announcement.id}`)
        }}
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: mainColor,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          textDecoration: 'underline',
          '&:hover': { opacity: 0.8 },
        }}
      >
        {t('platform-announcement.read-more', 'อ่านต่อ')}
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={dontShowAgain}
            onChange={(_, checked) => setDontShowAgain(checked)}
            sx={{ p: 0.5, color: mainColor, '&.Mui-checked': { color: mainColor } }}
          />
        }
        label={
          <Typography sx={{ fontSize: 12, color: mainColor, whiteSpace: 'nowrap' }}>
            {t('platform-announcement.dont-show-again', 'ไม่แสดงข้อความนี้อีก')}
          </Typography>
        }
        sx={{ ml: 0, mr: 0 }}
      />

      <IconButton
        size="small"
        onClick={() => onDismiss(announcement.id, dontShowAgain)}
        sx={{ color: mainColor }}
      >
        <CloseRoundedIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  )
}

export { PlatformAnnouncementBanner }
